import styled, { borderRadius, keyframes } from "@rentiohq/web-theme";
import { transparentize } from "polished";

// The Rentio house style (the style book the customer deck is built on) for the
// text and the animation: one deep green accent, ink titles and body-green
// text. The web theme has no tokens for these values yet, so they live here,
// together with the grays and the three window buttons of the drawn assistant
// window. The banner's surface, label and button use the dashboard's greens.
export const COLORS = {
  primary: "#0f7a5a",
  ink: "#0b211b",
  body: "#48605a",
  ring: "#dcefe9",
  border: "#dbe5e1",
  divider: "#cfdcd6",
  windowBar: "#f3f5f4",
  bubble: "#eef1f0",
  windowRed: "#ff5f57",
  windowYellow: "#febc2e",
  windowGreen: "#28c840",
};

// Below this width the animation is left out, so the banner stays one column
// of text instead of squeezing the drawing.
const COMPACT_BREAKPOINT = "1320px";

// On a phone the banner is little more than 200px wide, so the padding and
// the title shrink and the button text may wrap.
const PHONE_BREAKPOINT = "600px";

const enter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const PACKET_SIZE = 11;

const travel = keyframes`
  from {
    left: 0;
  }
  to {
    left: calc(100% - ${PACKET_SIZE}px);
  }
`;

const travelBack = keyframes`
  from {
    left: calc(100% - ${PACKET_SIZE}px);
  }
  to {
    left: 0;
  }
`;

const glow = (color: string, ellipse: string, strength: number) =>
  `radial-gradient(ellipse ${ellipse}, ${transparentize(
    1 - strength,
    color,
  )} 0%, ${transparentize(1, color)} 100%)`;

const glows = (colors: Record<string, unknown>) =>
  [
    glow(colors.green40 as string, "45% 90% at 100% 0%", 0.75),
    glow(colors.green50 as string, "40% 110% at 62% 115%", 0.55),
    glow(colors.green30 as string, "35% 80% at 0% 100%", 0.9),
    colors.green10 as string,
  ].join(", ");

export const Banner = styled.section`
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
  min-height: 200px;
  overflow: hidden;
  /* The lightest dashboard green with soft green glows in the corners and a
     green edge, so the banner stands apart from the white cards under it as an
     announcement, with their corners. */
  border: 1px solid ${({ theme }) => theme.getColor("primary", 200)};
  border-radius: ${borderRadius()};
  background: ${({ theme }) => glows(theme.colors)};
  /* Inter is the house font; without it the banner keeps the dashboard's own. */
  font-family: "Inter", ${({ theme }) => theme.fonts.primary};
  color: ${COLORS.body};
  animation: ${enter} 0.4s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    &,
    & * {
      animation: none !important;
      transition: none !important;
    }
  }
`;

// The text column has a fixed width, wide enough for the description to fit
// on two lines, and the animation takes the rest of the row.
export const Content = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 620px;
  padding: 20px 36px 20px 28px;

  @media (max-width: ${COMPACT_BREAKPOINT}) {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
  }

  @media (max-width: ${PHONE_BREAKPOINT}) {
    padding: 18px;
  }
`;

// A solid green label with a camera, so "webinar" is the first thing a broker
// reads and it is clear at once that the session is online.
export const Kicker = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px 4px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.getColor("primary", 800)};
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const Title = styled.h2`
  margin: 10px 0 0;
  font-size: 26px;
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.02em;
  white-space: nowrap;
  color: ${COLORS.ink};

  @media (max-width: ${COMPACT_BREAKPOINT}) {
    white-space: normal;
  }

  @media (max-width: ${PHONE_BREAKPOINT}) {
    font-size: 22px;
  }
`;

// The date and time under the title, so the banner reads as an event to sign
// up for rather than as a product feature. Semibold and close to the body size
// of the title, so it does not read as a footnote to it, on a phone included.
export const When = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  color: ${COLORS.ink};

  @media (max-width: ${PHONE_BREAKPOINT}) {
    white-space: normal;
  }

  svg {
    flex: none;
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.getColor("primary", 800)};
  }
`;

export const Description = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.body};
`;

// The standard dashboard button, so the banner's action looks like every other
// one in the app. It takes the dashboard font rather than the banner's, and may
// wrap on a phone, where the banner is too narrow for the full text.
export const Action = styled.div`
  margin-top: 16px;
  font-family: ${({ theme }) => theme.fonts.primary};

  @media (max-width: ${PHONE_BREAKPOINT}) {
    button {
      white-space: normal;
      text-align: center;
    }
  }
`;

export const Visual = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 28px 26px 16px;

  @media (max-width: ${COMPACT_BREAKPOINT}) {
    display: none;
  }
`;

export const Flow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 580px;
`;

// Wide enough for every example question to fit on one line, and tall enough
// for a lead line and three bullets under it.
export const Window = styled.div`
  flex: none;
  width: 340px;
  height: 152px;
  overflow: hidden;
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(11, 33, 27, 0.06);
`;

export const WindowBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 10px;
  border-bottom: 1px solid ${COLORS.border};
  background: ${COLORS.windowBar};
  font-size: 11px;
  color: ${COLORS.body};
`;

export const WindowDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

// Centered over the whole bar, as on a desktop window.
export const WindowTitle = styled.span`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
`;

export const WindowBody = styled.div`
  padding: 9px 12px;
  font-size: 12px;
  color: ${COLORS.ink};
`;

export const Question = styled.div`
  width: fit-content;
  margin-left: auto;
  padding: 5px 9px;
  border-radius: 8px;
  background: ${COLORS.bubble};
`;

export const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  color: ${COLORS.body};
`;

export const Spinner = styled.span`
  width: 9px;
  height: 9px;
  border: 2px solid ${COLORS.divider};
  border-top-color: ${COLORS.ink};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const Answer = styled.div`
  margin-top: 6px;
  line-height: 1.45;
`;

export const AnswerLine = styled.div<{ $isVisible: boolean }>`
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transform: translateY(${props => (props.$isVisible ? 0 : 3)}px);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
`;

export const Bullet = styled.span`
  display: inline-block;
  width: 3px;
  height: 3px;
  margin: 0 6px 2px 3px;
  border-radius: 50%;
  background: ${COLORS.ink};
  vertical-align: middle;
`;

export const Wire = styled.div`
  position: relative;
  flex: 1;
  min-width: 60px;
  height: 2px;
  background: ${COLORS.primary};
`;

// The connection, which grows its mint ring while the question travels.
export const Link = styled.span<{ $isPulsing: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${COLORS.primary};
  box-shadow: 0 0 0 ${props => (props.$isPulsing ? 10 : 6)}px ${COLORS.ring};
  transform: translate(-50%, -50%);
  transition: box-shadow 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
    color: #ffffff;
  }
`;

// The dot that carries the question to Rentio and the answer back. It is
// drawn under the connection, so it passes behind it.
export const Packet = styled.span<{
  $isReturning: boolean;
  $duration: number;
}>`
  position: absolute;
  top: 50%;
  width: ${PACKET_SIZE}px;
  height: ${PACKET_SIZE}px;
  margin-top: -${PACKET_SIZE / 2}px;
  border-radius: 50%;
  background: ${COLORS.primary};
  box-shadow: 0 0 0 3px ${COLORS.ring};
  animation: ${props => (props.$isReturning ? travelBack : travel)}
    ${props => props.$duration}ms ease-in-out forwards;
`;

export const RentioCard = styled.div<{ $isHit: boolean }>`
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 106px;
  height: 66px;
  border: 1px solid ${props => (props.$isHit ? COLORS.primary : COLORS.border)};
  border-radius: 12px;
  background: #ffffff;
  box-shadow: ${props =>
    props.$isHit ? `0 0 0 1px ${COLORS.primary}` : "none"};
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;

  img {
    width: auto;
    height: 18px;
  }
`;

export const Check = styled.span<{ $isVisible: boolean }>`
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${COLORS.primary};
  transform: scale(${props => (props.$isVisible ? 1 : 0)});
  transition: transform 0.3s cubic-bezier(0.3, 1.6, 0.5, 1);

  svg {
    width: 11px;
    height: 11px;
    color: #ffffff;
  }
`;

export const Found = styled.span<{ $isVisible: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 10px;
  white-space: nowrap;
  color: ${COLORS.body};
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;

  svg {
    width: 10px;
    height: 10px;
    color: ${COLORS.primary};
  }
`;
